import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { deleteAiPromptTemplate, getAiPromptTemplates } from 'services/apis/conditions'
import { Button, Input2 } from 'stories/components'
import { confirm } from 'utils'

import { AiPromptTemplateDetails } from './details'
import { AiPromptTemplate } from './type'

export function AiPromptTemplates() {
  const [query, setQuery] = useState('')
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [list, setList] = useState<AiPromptTemplate[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [selectedItem, setSelectedItem] = useState<AiPromptTemplate | null>(null)

  useEffect(() => refresh(), [])

  const filteredList = useMemo(() => {
    if (!query) return list
    return list.filter(({ title, prompt }) => {
      return [title, prompt].join(' ').toLowerCase().includes(query.trim().toLowerCase())
    })
  }, [query, list])

  const refresh = () => {
    setIsLoading(true)
    getAiPromptTemplates()
      .then(({ data }) => {
        setTotal(data?.length || 0)
        setList(data || [])
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <AiPromptTemplateDetails defaults={selectedItem} onBack={() => setIsEditing(false)} onComplete={refresh} />
    )

  const onAdd = () => {
    setSelectedItem(null)
    setIsEditing(true)
  }

  const onEdit = (item: AiPromptTemplate) => {
    setSelectedItem(item)
    setIsEditing(true)
  }

  const onTrash = async (item: AiPromptTemplate) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this template?
        <br />
        <span className="text-gray-600">Title: {item.title}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)
    deleteAiPromptTemplate(item.id)
      .then(refresh)
      .catch(() => setIsLoading(false))
  }

  return (
    <div className="List-container relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold flex items-center mb-3">AI Prompt Templates</h2>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {total} Items</p>
        </div>
        <div className="flex justify-center items-center gap-3">
          <Button full onClick={onAdd}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Title
              </th>
              <th scope="col" className="px-3">
                Prompt
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {filteredList.map((item, index) => (
              <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={item.id}>
                <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {index + 1}
                </td>
                <td className="px-3">{item.title}</td>
                <td className="px-3 py-2">
                  <code>
                    <div className="max-w-md truncate">{item.prompt}</div>
                  </code>
                </td>
                <td className="px-3">
                  <span className="flex">
                    <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => onEdit(item)}>
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onTrash(item)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export * from './type'
